import React, { useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import Hero from "../components/sections/Hero";
import ProcessSection from "../components/sections/ProcessSection";
import Services from "../components/sections/Services";
import Portfolio from "../components/sections/Portfolio";
import CallToAction from "../components/sections/CallToAction";
import Testimonials from "../components/sections/Testimonials";
import KeyMetrics from "../components/sections/KeyMetrics";
import GridOverlay from "../components/ui/GridOverlay";
import SEO from "../components/SEO";
import FAQ from "../components/sections/FAQ";

const Home = () => {
    const location = useLocation();
    const initialMount = useRef(true);

    useEffect(() => {
        const savedPosition = sessionStorage.getItem("portfolioScrollY");
        if (savedPosition > 0) {
            setTimeout(() => {
                window.scrollTo(0, parseInt(savedPosition));
            }, 100);
        }
    }, []);

    // Improved hash navigation - runs on mount and location changes
    useEffect(() => {
        // Do not interfere with the stored position on initial render
        if (initialMount.current) {
            initialMount.current = false;
            return;
        }

        // Process hash navigation
        if (location.hash) {
            // Give DOM more time to fully render
            const timer = setTimeout(() => {
                const hash = location.hash.replace("#", "");
                const sectionId = hash.split("?")[0]; // Remove query params

                console.log("Attempting to scroll to:", sectionId);

                const element = document.getElementById(sectionId);
                if (element) {
                    console.log("Found element, scrolling...");
                    element.scrollIntoView({ behavior: "smooth" });

                    // Handle service selection if needed
                    if (hash.includes("?service=")) {
                        const params = new URLSearchParams(hash.split("?")[1]);
                        const serviceId = params.get("service");
                        if (serviceId) {
                            window.dispatchEvent(
                                new CustomEvent("serviceSelected", {
                                    detail: { serviceId },
                                })
                            );
                        }
                    }
                } else {
                    console.log("Element not found:", sectionId);
                }
            }, 500); // Increased delay

            return () => clearTimeout(timer);
        }
    }, [location]);

    // Enhanced structured data for better SEO
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Nyloria",
        url: "https://www.nyloria.fr/",
        logo: "https://www.nyloria.fr/images/logo.png",
        description:
            "Agence experte en création de sites web responsive, développement d'applications iOS & Android, e-commerce et optimisation SEO.",
        address: {
            "@type": "PostalAddress",
            streetAddress: "123 Avenue de la Tech",
            addressLocality: "Paris",
            postalCode: "75001",
            addressCountry: "FR",
        },
        contactPoint: {
            "@type": "ContactPoint",
            telephone: "+33-1-23-45-67-89",
            contactType: "customer service",
            availableLanguage: ["French", "English"],
        },
        sameAs: [
            "https://www.facebook.com/nextiaagence",
            "https://www.linkedin.com/company/nyloria-france",
            "https://twitter.com/nextiaagence",
            "https://www.instagram.com/nextiaagence/",
            "https://github.com/nyloria",
        ],
    };

    // Add website schema with enhanced SiteLinks SearchBox
    const websiteSchema = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Nyloria",
        alternateName: "Nyloria",
        url: "https://www.nyloria.fr/",
        potentialAction: {
            "@type": "SearchAction",
            target: "https://www.nyloria.fr/search?q={search_term_string}",
            "query-input": "required name=search_term_string",
        },
    };

    // Add SiteNavigationElement schema for sitelinks
    const siteNavigationSchema = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: [
            {
                "@type": "SiteNavigationElement",
                position: 1,
                name: "Services",
                description: "Découvrez nos services de création web et mobile",
                url: "https://www.nyloria.fr/#services?service=sitevitrine"
            },
            {
                "@type": "SiteNavigationElement",
                position: 2,
                name: "Blog",
                description: "Actualités et conseils sur le les sites web et applications mobile",
                url: "https://www.nyloria.fr/blog"
            },
            {
                "@type": "SiteNavigationElement",
                position: 3,
                name: "Portfolio",
                description: "Explorez nos réalisations récentes",
                url: "https://www.nyloria.fr/#portfolio"
            },
            {
                "@type": "SiteNavigationElement",
                position: 4,
                name: "Contact",
                description: "Contactez notre agence pour un devis gratuit",
                url: "https://www.nyloria.fr/contact"
            },
        ]
    };

    // Add LocalBusiness schema for better local SEO
    const localBusinessSchema = {
        "@context": "https://schema.org",
        "@id": "https://www.nyloria.fr",
        "@type": "LocalBusiness",
        name: "Nyloria",
        description: "Agence web spécialisée en création de sites internet et applications mobiles",
        url: "https://www.nyloria.fr",
        image: "https://www.nyloria.fr/images/logo.png",
        sameAs: [
            "https://www.linkedin.com/company/nyloria",
        ]
    };

    return (
        <>
            <Helmet>
                <title>
                    Création de sites web et applications mobiles
                </title>
                <meta
                    name="description"
                    content="Agence web spécialisée en développement de sites internet personnalisés, conception d'applications iOS & Android, solutions e-commerce Shopify et WordPress, refonte de site et référencement local. Devis gratuit sous 48h."
                />
                <meta
                    name="keywords"
                    content="création site web, applications iOS & Android, e-commerce, optimisation SEO, site responsive, applications mobiles, design UI/UX, stratégie digitale, agence web, agence digitale"
                />
                <link rel="canonical" href="https://www.nyloria.fr/" />
                <meta
                    property="og:title"
                    content="Création de sites web et applications mobiles"
                />
                <meta
                    property="og:description"
                    content="Agence web spécialisée en développement de sites internet personnalisés, conception d'applications iOS & Android, solutions e-commerce Shopify et WordPress, refonte de site et référencement local. Devis gratuit sous 48h."
                />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://www.nyloria.fr/" />
                <meta name="twitter:card" content="summary_large_image" />
                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(websiteSchema)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(siteNavigationSchema)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(localBusinessSchema)}
                </script>
            </Helmet>
            <GridOverlay />
            <main>
                <Hero />
                <Services />
                <Portfolio />
                <ProcessSection />
                <KeyMetrics />
                <CallToAction />
                <FAQ />
                <Testimonials />
            </main>
        </>
    );
};

export default Home;
